import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { fmt, fmtDate } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { PREPAID_ASSET_CODE, getCat, useDateMode, type PPItem } from './pp-helpers';
import { PPModifyModal } from './PPModifyModal';
import { PPSuspendModal } from './PPSuspendModal';
import { PPTerminateModal } from './PPTerminateModal';
import { downloadFile } from '../../lib/api';
import { usePrepaidSchedule, useResumePrepaidContract } from '../../lib/queries';
import { exportRows, todayStamp, type ExportColumn } from '../../lib/xlsx';

interface ScheduleRow {
  i: number;
  month: string;
  date: string;
  amount: number;
  balance: number;
  status: 'posted' | 'current' | 'pending';
  jv: string | null;
}

// Export columns for the schedule Excel export (Change E).
const SCHEDULE_EXPORT_COLS: ExportColumn<ScheduleRow>[] = [
  { header: 'งวด', value: (s) => s.i, width: 8 },
  { header: 'เดือน', value: (s) => s.month, width: 14 },
  { header: 'วันที่ตัดจ่าย', value: (s) => s.date, width: 14 },
  { header: 'จำนวนเงิน', value: (s) => s.amount, width: 14 },
  { header: 'คงเหลือยังไม่ตัด', value: (s) => Math.max(0, s.balance), width: 16 },
  { header: 'เลขที่ JV', value: (s) => s.jv ?? '', width: 20 },
  { header: 'สถานะ', value: (s) => s.status, width: 12 },
];

export interface ScheduleProps {
  item: PPItem;
  setItem: (i: PPItem) => void;
  companyId: string | undefined;
  items: readonly PPItem[];
}

export function PPSchedule({ item, setItem, companyId, items }: ScheduleProps) {
  const cat = getCat(item.cat);
  const { df } = useDateMode();

  // Lifecycle (P6) — actions gated by the contract's real status (apiStatus).
  const resumeMut = useResumePrepaidContract(companyId);
  const [lifecycle, setLifecycle] = useState<'terminate' | 'modify' | 'suspend' | null>(null);
  const canAct = Boolean(item.apiId);
  const isActive = item.apiStatus === 'ACTIVE';
  const isSuspended = item.apiStatus === 'SUSPENDED';
  const isFrozen = item.apiStatus === 'TERMINATED' || item.apiStatus === 'FULLY_AMORTIZED';

  // For API-backed contracts: fetch the server-projected schedule (exact amounts,
  // real statuses, real journalEntryIds). Replaces the old client-side even-monthly
  // projection that was noted as a P4 TODO.
  const { data: apiSchedule } = usePrepaidSchedule(companyId, item.apiId);

  // For non-API (mock) rows: keep the original client-side projection.
  const schedule = useMemo<ScheduleRow[]>(() => {
    if (item.apiId && apiSchedule) {
      return apiSchedule.map((s) => {
        const d = new Date(s.voucherDate);
        return {
          i: s.index,
          month: d.toLocaleDateString('th-TH', { month: 'short', year: '2-digit' }),
          date: d.toISOString().slice(0, 10),
          amount: Number(s.amount),
          balance: Number(s.closingBalance),
          status: s.status,
          jv: s.journalEntryId ?? null,
        };
      });
    }

    // Fallback: client-side projection from PPItem (mock rows or while API schedule loads).
    const startDate = new Date(item.start);
    const rows: ScheduleRow[] = [];
    let bal = item.total;
    for (let i = 0; i < item.period; i++) {
      const d = new Date(startDate.getFullYear(), startDate.getMonth() + i, 28);
      const month = d.toLocaleDateString('th-TH', { month: 'short', year: '2-digit' });
      bal -= item.monthly;
      let status: ScheduleRow['status'];
      if (i < item.used) status = 'posted';
      else if (i === item.used) status = 'current';
      else status = 'pending';
      rows.push({
        i: i + 1,
        month,
        date: d.toISOString().slice(0, 10),
        amount: item.monthly,
        balance: bal,
        status,
        jv: null,
      });
    }
    return rows;
  }, [item, apiSchedule]);

  const displayPeriod = apiSchedule ? apiSchedule.length : item.period;

  async function handleExcel(): Promise<void> {
    await exportRows(
      `ตารางตัดจ่าย_${item.id}_${todayStamp()}.xlsx`,
      'ตารางตัดจ่าย',
      SCHEDULE_EXPORT_COLS,
      schedule,
    );
  }

  return (
    <>
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <select
              className="select"
              style={{ width: 360, height: 36, fontSize: 14 }}
              value={item.id}
              onChange={(e) => {
                const next = items.find((p) => p.id === e.target.value);
                if (next) setItem(next);
              }}
            >
              {items.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.id} — {p.vendor.slice(0, 28)}
                </option>
              ))}
            </select>
            <span className="badge brand" style={{ height: 26 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: cat.tone,
                  marginRight: 4,
                }}
              />
              {cat.label}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              className="btn sm"
              disabled={!item.apiId}
              onClick={() => {
                // P8: server-rendered approver-stamped certificate (Sarabun PDF).
                if (companyId && item.apiId) {
                  void downloadFile(
                    `/companies/${companyId}/prepaid-contracts/${item.apiId}/certificate.pdf`,
                    `prepaid-cert-${item.id}.pdf`,
                  );
                }
              }}
            >
              <Icon name="print" size={12} /> พิมพ์ใบรับรอง
            </button>
            <button className="btn sm" onClick={() => void handleExcel()}>
              <Icon name="download" size={12} /> Excel
            </button>
            {/* Lifecycle (P6) — modify/re-spread, suspend/resume, terminate. Gated
                by the contract's real status; hidden for mock rows (no apiId). */}
            {canAct && isActive && (
              <button className="btn sm" onClick={() => setLifecycle('modify')}>
                ปรับปรุงตาราง
              </button>
            )}
            {canAct && isActive && (
              <button className="btn sm" onClick={() => setLifecycle('suspend')}>
                พักสัญญา
              </button>
            )}
            {canAct && isSuspended && (
              <button
                className="btn sm"
                disabled={resumeMut.isPending}
                onClick={() => item.apiId && resumeMut.mutate(item.apiId)}
              >
                {resumeMut.isPending ? 'กำลังเปิด…' : 'เปิดใช้ใหม่'}
              </button>
            )}
            {canAct && !isFrozen && (
              <button className="btn sm" onClick={() => setLifecycle('terminate')}>
                ยกเลิกสัญญา
              </button>
            )}
            {/* GL drill-through: link to /ledger filtered by the prepaid-asset control account */}
            {/* TODO link to GL with period filter once ledger supports it */}
            <Link to="/ledger" search={{ accountCode: PREPAID_ASSET_CODE }} className="btn sm">
              ดูใน GL
            </Link>
          </div>
        </div>
        <div style={{ padding: '16px 18px' }}>
          <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>{item.desc}</div>
          <div className="meta">
            ผู้ขาย: <b style={{ color: '#3f3f46' }}>{item.vendor}</b> · จ่ายเมื่อ{' '}
            {fmtDate(item.acquired)} (อ้างอิง {item.payRef}) · บัญชีค่าใช้จ่าย: {cat.acct}
          </div>

          <div
            style={{
              marginTop: 16,
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: 18,
            }}
          >
            <div>
              <div className="meta">มูลค่าสัญญา</div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  marginTop: 4,
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(item.total)}
              </div>
            </div>
            <div>
              <div className="meta">ตัดจ่าย/เดือน</div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  marginTop: 4,
                  color: 'var(--brand)',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(item.monthly)}
              </div>
            </div>
            <div>
              <div className="meta">งวดตัดจ่าย</div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  marginTop: 4,
                  fontFamily: 'var(--font-display)',
                }}
              >
                {displayPeriod} เดือน
              </div>
            </div>
            <div>
              <div className="meta">ตัดไปแล้ว</div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  marginTop: 4,
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(item.monthly * item.used)}
              </div>
              <div className="meta">
                {item.used} งวด · {Math.round((item.used / item.period) * 100)}%
              </div>
            </div>
            <div>
              <div className="meta">คงเหลือยังไม่ตัด</div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  marginTop: 4,
                  color: 'var(--positive)',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(item.monthly * item.remain)}
              </div>
              <div className="meta">{item.remain} งวด</div>
            </div>
            <div>
              <div className="meta">ช่วงเวลา</div>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>{df(item.start)}</div>
              <div className="meta">ถึง {df(item.end)}</div>
            </div>
          </div>

          <div
            style={{
              marginTop: 16,
              height: 10,
              borderRadius: 5,
              background: '#f0f0f3',
              overflow: 'hidden',
              display: 'flex',
            }}
          >
            <div style={{ width: `${(item.used / item.period) * 100}%`, background: cat.tone }} />
          </div>
        </div>
      </div>

      <div className="tbl-shell">
        <div className="filter-row">
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            ตารางตัดจ่ายรายเดือน — {displayPeriod} งวด
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <span className="chip">
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'var(--positive)',
                }}
              />{' '}
              Posted แล้ว ({schedule.filter((s) => s.status === 'posted').length})
            </span>
            <span className="chip active">
              <span
                style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--brand)' }}
              />{' '}
              งวดนี้ ({schedule.filter((s) => s.status === 'current').length})
            </span>
            <span className="chip">
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'var(--border-strong)',
                }}
              />{' '}
              รอประมวลผล ({schedule.filter((s) => s.status === 'pending').length})
            </span>
          </div>
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 540 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 60 }}>งวด</th>
                <th style={{ width: 110 }}>เดือน</th>
                <th style={{ width: 110 }}>วันที่ตัดจ่าย</th>
                <th>คำอธิบาย</th>
                <th>เลขที่ JV</th>
                <th>เดบิต</th>
                <th>เครดิต</th>
                <th className="num">จำนวนเงิน</th>
                <th className="num">คงเหลือยังไม่ตัด</th>
                <th>สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((s) => (
                <tr
                  key={s.i}
                  style={s.status === 'current' ? { background: 'var(--brand-tint)' } : undefined}
                >
                  <td className="num">{s.i}</td>
                  <td>{s.month}</td>
                  <td>{df(s.date)}</td>
                  <td>
                    ตัดจ่ายค่าใช้จ่ายล่วงหน้า งวด {s.i}/{displayPeriod} — {item.vendor.slice(0, 30)}
                  </td>
                  <td className="code">
                    {s.jv ? (
                      // TODO link to JV detail — /journal has no single-JV deep-link URL param yet
                      <Link to="/journal" style={{ color: 'var(--brand)', fontWeight: 500 }}>
                        {s.jv}
                      </Link>
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td className="code muted">{cat.acct.split(' ')[0]}</td>
                  <td className="code muted">{PREPAID_ASSET_CODE}</td>
                  <td className="num tnum">{fmt(s.amount)}</td>
                  <td className="num tnum">{fmt(Math.max(0, s.balance))}</td>
                  <td>
                    {s.status === 'posted' ? (
                      <span className="badge positive">
                        <span className="bdot" />
                        ผ่านรายการแล้ว
                      </span>
                    ) : s.status === 'current' ? (
                      <span className="badge brand">
                        <span className="bdot" />
                        งวดปัจจุบัน
                      </span>
                    ) : (
                      <span className="badge neutral">
                        <span className="bdot" />
                        รอประมวลผล
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="total">
                <td colSpan={7}>รวมตัดจ่ายทั้งสิ้น {displayPeriod} งวด</td>
                <td className="num tnum">{fmt(item.total)}</td>
                <td colSpan={2} />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {lifecycle === 'terminate' && (
        <PPTerminateModal item={item} companyId={companyId} onClose={() => setLifecycle(null)} />
      )}
      {lifecycle === 'modify' && (
        <PPModifyModal item={item} companyId={companyId} onClose={() => setLifecycle(null)} />
      )}
      {lifecycle === 'suspend' && (
        <PPSuspendModal item={item} companyId={companyId} onClose={() => setLifecycle(null)} />
      )}
    </>
  );
}
